import { useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Col,
  Select,
  Spin,
  Switch,
  Tag,
  Typography,
} from "antd";
import { Calendar, TicketPercent, Users } from "lucide-react";
import dayjs from "dayjs";
import {
  createDiscountCode,
  getDiscountCode,
  updateDiscountCode,
} from "services/discountCodes.service";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import useAuthStore from "stores/AuthStore/AuthStore";
import useDiscountCodesConfig from "hooks/useDiscountCodesConfig/useDiscountCodesConfig";
import { SummaryRow, SummarySection } from "Components/SummaryRows/SummaryRows";
import useVendorEvents from "../commons/useVendorEvents";
import {
  AUDIENCE,
  LIMITS_SCOPE_LABEL,
  buildCodeBody,
  buildEventOptions,
  canShowCombine,
  diffCodeBody,
  formatDiscount,
  getCodeFieldError,
  getExpiryDefaultTime,
  getKeepMembershipExtra,
  getNewCodeDefaults,
  mapDetailToFormValues,
  resolveFormCommunityId,
  toCreateBody,
} from "../commons/discountCodes.helpers";
import { CODES_COPY } from "../commons/discountCodes.copy";

const { Text } = Typography;

const DATE_FORMAT = "MMM D, YYYY HH:mm";
const CODE_NAME_PATTERN = /^[A-Za-z0-9]{4,20}$/;

function SectionTitle({ children }) {
  return (
    <Divider orientation="left" plain>
      <span className="text-sm font-semibold">{children}</span>
    </Divider>
  );
}

/**
 * Create / edit a discount code in two steps: form, then review
 * (ui-spec §2.1.3). `codeId` switches to edit mode. `defaultEventId` and
 * `defaultCommunityId` pre-tick an event (Event detail tab).
 */
function CodeFormModal({
  open,
  onClose,
  codeId,
  defaultEventId,
  defaultCommunityId,
  onSaved,
}) {
  const [form] = Form.useForm();
  const { isRootUser } = useAuthStore();
  const { policy, communities } = useDiscountCodesConfig();
  const { events, loading: loadingEvents } = useVendorEvents({
    enabled: open,
  });
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const isEdit = Boolean(codeId);
  const showCommunityField = isRootUser && communities.length > 1;
  const showCombine = canShowCombine(policy);

  const communityField = Form.useWatch("community_id", form);
  const discountType = Form.useWatch("discount_type", form);
  const audience = Form.useWatch("audience", form);
  const keepMembership = Form.useWatch("keep_membership_discount", form);
  const pickedIds = Form.useWatch("event_ids", form) ?? [];

  const communityId = resolveFormCommunityId({
    communityField: showCommunityField ? communityField : null,
    pickedIds,
    events,
    communities,
  });
  const community = communities.find((c) => c.community_id === communityId);
  const hasPlans = community?.has_membership_plans === true;
  const communityName = community?.display_name;

  const eventOptions = useMemo(
    () =>
      buildEventOptions(events, {
        communityId: showCommunityField ? (communityField ?? null) : null,
        pickedIds,
      }),
    // pickedIds is a new array on each render; compare by content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, showCommunityField, communityField, pickedIds.join(",")]
  );

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setDetail(null);
    form.resetFields();
    if (!codeId) {
      form.setFieldsValue(
        getNewCodeDefaults({
          eventId: defaultEventId,
          communityId: defaultCommunityId,
        })
      );
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    getDiscountCode(codeId).then(({ data, errors }) => {
      if (cancelled) return;
      setLoadingDetail(false);
      if (errors?.length) {
        renderErrorNotifications(errors);
        onClose();
        return;
      }
      setDetail(data);
      form.setFieldsValue(mapDetailToFormValues(data));
    });
    return () => {
      cancelled = true;
    };
  }, [open, codeId, defaultEventId, defaultCommunityId, form]); // eslint-disable-line react-hooks/exhaustive-deps

  // Without membership plans only "Everyone" applies and the switch stays on
  // (R-12, D6). Wait for the events and the code so nothing resets early.
  const ready = !loadingEvents && !loadingDetail;
  useEffect(() => {
    if (!open || !ready || hasPlans) return;
    if (audience !== AUDIENCE.EVERYONE || keepMembership === false) {
      form.setFieldsValue({
        audience: AUDIENCE.EVERYONE,
        keep_membership_discount: true,
      });
    }
  }, [open, ready, hasPlans, audience, keepMembership, form]);

  const handleValuesChange = (changed) => {
    if ("community_id" in changed) {
      form.setFieldsValue({ event_ids: [], audience: AUDIENCE.EVERYONE });
    }
  };

  const ctx = { hasMembershipPlans: hasPlans, showCombine };
  const isStarted =
    isEdit && detail?.starts_at && dayjs(detail.starts_at).isBefore(dayjs());

  const handleReview = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    setStep(1);
  };

  const handleSave = async () => {
    const values = form.getFieldsValue(true);
    const body = buildCodeBody(values, ctx);
    let result;
    if (isEdit) {
      const initial = buildCodeBody(mapDetailToFormValues(detail), ctx);
      const changes = diffCodeBody(initial, body);
      if (Object.keys(changes).length === 0) {
        onClose();
        return;
      }
      setSaving(true);
      result = await updateDiscountCode(codeId, changes);
    } else {
      setSaving(true);
      result = await createDiscountCode(toCreateBody(body));
    }
    setSaving(false);
    const { data, errors } = result;
    if (errors?.length) {
      const fieldError = getCodeFieldError(errors, body.code);
      if (fieldError) {
        setStep(0);
        form.setFields([
          { name: fieldError.name, errors: [fieldError.message] },
        ]);
        return;
      }
      renderErrorNotifications(errors);
      return;
    }
    renderSuccessNotifications(
      isEdit
        ? {
            title: CODES_COPY.toastUpdatedTitle,
            message: CODES_COPY.toastUpdatedMessage,
          }
        : {
            title: CODES_COPY.toastCreatedTitle,
            message: CODES_COPY.toastCreatedMessage(body.code),
          }
    );
    onSaved?.(data);
    onClose();
  };

  const audienceOptions = [
    { value: AUDIENCE.EVERYONE, label: CODES_COPY.audienceEveryone },
    {
      value: AUDIENCE.MEMBERS,
      label: CODES_COPY.audienceMembersOf(communityName),
      disabled: !hasPlans,
    },
    {
      value: AUDIENCE.NON_MEMBERS,
      label: CODES_COPY.audienceNonMembersOnly,
      disabled: !hasPlans,
    },
  ];

  const audienceExtra = !hasPlans
    ? CODES_COPY.audienceDisabled
    : audience === AUDIENCE.MEMBERS
      ? CODES_COPY.audienceMembersExtra(communityName)
      : audience === AUDIENCE.NON_MEMBERS
        ? CODES_COPY.audienceNonMembersExtra(communityName)
        : undefined;

  const footer =
    step === 0
      ? [
          <Button key="cancel" type="text" onClick={onClose}>
            {CODES_COPY.btnCancel}
          </Button>,
          <Button
            key="review"
            type="primary"
            onClick={handleReview}
            disabled={loadingDetail}
          >
            {CODES_COPY.btnReview}
          </Button>,
        ]
      : [
          <Button key="back" onClick={() => setStep(0)} disabled={saving}>
            {CODES_COPY.btnBack}
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={handleSave}
          >
            {isEdit ? CODES_COPY.btnSave : CODES_COPY.btnCreate}
          </Button>,
        ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={680}
      destroyOnHidden
      title={
        <div>
          <div>
            {isEdit ? CODES_COPY.formTitleEdit : CODES_COPY.formTitleCreate}
          </div>
          <Text type="secondary" className="text-sm font-normal">
            {CODES_COPY.formSubtitle}
          </Text>
        </div>
      }
      footer={footer}
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
    >
      {loadingDetail && (
        <div className="flex items-center justify-center min-h-[200px]">
          <Spin size="large" />
        </div>
      )}
      <div hidden={loadingDetail}>
        {isEdit && detail?.uses_count > 0 && step === 0 && (
          <div className="rounded-xl border border-border bg-muted px-4 py-3 mb-4 text-sm text-foreground">
            {CODES_COPY.editNotice}
          </div>
        )}

        <div hidden={step !== 0}>
          <Form
            form={form}
            layout="vertical"
            className="pt-2"
            onValuesChange={handleValuesChange}
          >
            {showCommunityField && (
              <Row gutter={[24, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="community_id"
                    label={CODES_COPY.fieldCommunity}
                    extra={CODES_COPY.fieldCommunityExtra}
                    rules={[
                      {
                        required: true,
                        message: CODES_COPY.fieldCommunityRequired,
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder={CODES_COPY.communityPlaceholder}
                      disabled={isEdit}
                      options={communities.map((c) => ({
                        value: c.community_id,
                        label: c.display_name,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <SectionTitle>{CODES_COPY.sectionCode}</SectionTitle>
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="code"
                  label={CODES_COPY.fieldName}
                  extra={CODES_COPY.fieldNameExtra}
                  normalize={(v) => (v ?? "").toUpperCase()}
                  rules={[
                    {
                      required: true,
                      message: CODES_COPY.fieldNameRequired,
                    },
                    {
                      pattern: CODE_NAME_PATTERN,
                      message: CODES_COPY.fieldNameFormat,
                    },
                  ]}
                >
                  <Input
                    placeholder={CODES_COPY.fieldNamePlaceholder}
                    maxLength={20}
                    showCount
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="discount_type"
                  label={CODES_COPY.fieldDiscount}
                >
                  <Radio.Group buttonStyle="solid">
                    <Radio.Button value="PERCENT">% off</Radio.Button>
                    <Radio.Button value="FLAT">₹ off</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              {discountType === "FLAT" ? (
                <Col xs={24} md={12}>
                  <Form.Item
                    name="flat_value"
                    label={CODES_COPY.fieldFlat}
                    rules={[
                      { required: true, message: CODES_COPY.fieldFlatErr },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      prefix="₹"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              ) : (
                <>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="percent_value"
                      label={CODES_COPY.fieldPercent}
                      rules={[
                        {
                          required: true,
                          message: CODES_COPY.fieldPercentErr,
                        },
                        {
                          type: "number",
                          min: 1,
                          max: 100,
                          message: CODES_COPY.fieldPercentErr,
                        },
                      ]}
                    >
                      <InputNumber
                        min={1}
                        max={100}
                        suffix="%"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="max_per_ticket"
                      label={CODES_COPY.fieldMaxPerTicket}
                      extra={CODES_COPY.fieldMaxPerTicketExtra}
                    >
                      <InputNumber
                        min={1}
                        precision={0}
                        prefix="₹"
                        placeholder={CODES_COPY.fieldMaxPerTicketPlaceholder}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>

            <SectionTitle>{CODES_COPY.sectionAudience}</SectionTitle>
            <Form.Item
              name="audience"
              label={CODES_COPY.fieldAudience}
              extra={audienceExtra}
            >
              <Radio.Group
                buttonStyle="solid"
                options={audienceOptions}
                optionType="button"
              />
            </Form.Item>
            {audience !== AUDIENCE.NON_MEMBERS && (
              <Form.Item
                name="keep_membership_discount"
                label={CODES_COPY.fieldKeepMembership}
                valuePropName="checked"
                tooltip={CODES_COPY.keepMembershipTooltip}
                extra={
                  <span aria-live="polite">
                    {getKeepMembershipExtra(policy)}
                  </span>
                }
              >
                <Switch
                  disabled={!hasPlans}
                  aria-label={CODES_COPY.fieldKeepMembership}
                />
              </Form.Item>
            )}

            <SectionTitle>{CODES_COPY.sectionEvents}</SectionTitle>
            <Form.Item
              name="event_ids"
              label={CODES_COPY.fieldEvents}
              extra={CODES_COPY.fieldEventsExtra}
              rules={[
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message: CODES_COPY.fieldEventsErr,
                },
              ]}
            >
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                loading={loadingEvents}
                placeholder={CODES_COPY.fieldEventsPlaceholder}
                options={eventOptions}
                optionRender={(option) => (
                  <span className="flex items-center justify-between gap-2">
                    <span>
                      {option.data.label}
                      {option.data.otherCommunity && (
                        <Text type="secondary" className="text-xs">
                          {" "}
                          · {CODES_COPY.eventOtherCommunity}
                        </Text>
                      )}
                    </span>
                    {option.data.isPast && <Tag>{CODES_COPY.eventPast}</Tag>}
                  </span>
                )}
              />
            </Form.Item>

            <SectionTitle>{CODES_COPY.sectionLimits}</SectionTitle>
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item name="total_uses" label={CODES_COPY.fieldTotalUses}>
                  <InputNumber
                    min={1}
                    precision={0}
                    placeholder={CODES_COPY.fieldTotalUsesPlaceholder}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="uses_per_person"
                  label={CODES_COPY.fieldPerPerson}
                  extra={CODES_COPY.fieldPerPersonExtra}
                  rules={[
                    { required: true, message: CODES_COPY.fieldPerPersonErr },
                  ]}
                >
                  <InputNumber
                    min={1}
                    precision={0}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              {pickedIds.length >= 2 && (
                <Col xs={24} md={12}>
                  <Form.Item
                    name="limits_scope"
                    label={CODES_COPY.fieldLimitsCount}
                  >
                    <Radio.Group buttonStyle="solid">
                      <Radio.Button value="PER_EVENT">
                        {CODES_COPY.limitsPerEvent}
                      </Radio.Button>
                      <Radio.Button value="CODE">
                        {CODES_COPY.limitsAcrossAll}
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={12}>
                <Form.Item
                  name="max_discounted_tickets"
                  label={CODES_COPY.fieldTicketsPerOrder}
                  extra={CODES_COPY.fieldTicketsPerOrderExtra}
                >
                  <InputNumber
                    min={1}
                    precision={0}
                    placeholder={CODES_COPY.fieldTicketsPerOrderPlaceholder}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <SectionTitle>{CODES_COPY.sectionDates}</SectionTitle>
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="starts_at"
                  label={CODES_COPY.fieldStarts}
                  extra={CODES_COPY.fieldStartsExtra}
                >
                  <DatePicker
                    showTime={{ format: "HH:mm" }}
                    format={DATE_FORMAT}
                    disabled={isStarted}
                    disabledDate={(d, info) =>
                      !isEdit && info?.type === "date"
                        ? d && d < dayjs().startOf("day")
                        : false
                    }
                    style={{ width: "100%" }}
                    onChange={() => form.validateFields(["expires_at"])}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="expires_at"
                  label={CODES_COPY.fieldExpires}
                  extra={CODES_COPY.fieldExpiresExtra}
                  rules={[
                    { required: true, message: CODES_COPY.fieldExpiresErr },
                    {
                      validator(_, v) {
                        const start = form.getFieldValue("starts_at");
                        if (v && start && !dayjs(v).isAfter(start))
                          return Promise.reject(
                            new Error(CODES_COPY.fieldExpiresBeforeStart)
                          );
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <DatePicker
                    showTime={{
                      format: "HH:mm",
                      defaultOpenValue: getExpiryDefaultTime(),
                    }}
                    format={DATE_FORMAT}
                    disabledDate={(d, info) =>
                      info?.type === "date"
                        ? d && d < dayjs().startOf("day")
                        : false
                    }
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {showCombine && (
              <Form.Item
                name="can_combine"
                label={CODES_COPY.fieldCanCombine}
                extra={CODES_COPY.fieldCanCombineExtra}
                valuePropName="checked"
              >
                <Switch aria-label={CODES_COPY.fieldCanCombine} />
              </Form.Item>
            )}

            <SectionTitle>{CODES_COPY.sectionNotes}</SectionTitle>
            <Form.Item
              name="private_note"
              label={CODES_COPY.fieldNote}
              extra={CODES_COPY.fieldNoteExtra}
            >
              <Input.TextArea
                rows={2}
                maxLength={200}
                showCount
                placeholder={CODES_COPY.fieldNotePlaceholder}
              />
            </Form.Item>
          </Form>
        </div>

        {step === 1 && (
          <CodeReview
            values={form.getFieldsValue(true)}
            ctx={ctx}
            events={events}
            communityName={communityName}
          />
        )}

        <Text type="secondary" className="block pt-2 text-xs">
          {CODES_COPY.formFooter}
        </Text>
      </div>
    </Modal>
  );
}

/** Step 2: every field as it will be saved. */
function CodeReview({ values, ctx, events, communityName }) {
  const body = buildCodeBody(values, ctx);
  const eventNames = body.event_ids
    .map((id) => events.find((e) => e.id === id)?.name ?? id)
    .join(", ");
  const audienceLabel = {
    EVERYONE: CODES_COPY.audienceEveryone,
    MEMBERS: CODES_COPY.audienceMembersOf(communityName),
    NON_MEMBERS: CODES_COPY.audienceNonMembersOnly,
  }[body.audience];
  const fmt = (v) => (v ? v.format(DATE_FORMAT) : "");

  return (
    <div className="space-y-4 pt-2">
      <p className="text-sm font-semibold">{CODES_COPY.reviewTitle}</p>
      <SummarySection icon={TicketPercent} title={CODES_COPY.sectionCode}>
        <SummaryRow label={CODES_COPY.fieldName} value={body.code} />
        <SummaryRow
          label={CODES_COPY.fieldDiscount}
          value={formatDiscount(body.discount)}
        />
      </SummarySection>
      <SummarySection icon={Users} title={CODES_COPY.sectionAudience}>
        <SummaryRow label={CODES_COPY.fieldAudience} value={audienceLabel} />
        <SummaryRow
          label={CODES_COPY.reviewKeepMembership}
          value={body.keep_membership_discount ? "Yes" : "No"}
        />
        <SummaryRow label={CODES_COPY.fieldEvents} value={eventNames} />
      </SummarySection>
      <SummarySection icon={Calendar} title={CODES_COPY.sectionLimits}>
        <SummaryRow
          label={CODES_COPY.fieldTotalUses}
          value={body.total_uses ?? CODES_COPY.fieldTotalUsesPlaceholder}
        />
        <SummaryRow
          label={CODES_COPY.fieldPerPerson}
          value={body.uses_per_person}
        />
        <SummaryRow
          label={CODES_COPY.fieldLimitsCount}
          value={LIMITS_SCOPE_LABEL[body.limits_scope]}
        />
        <SummaryRow
          label={CODES_COPY.fieldTicketsPerOrder}
          value={
            body.max_discounted_tickets ??
            CODES_COPY.fieldTicketsPerOrderPlaceholder
          }
        />
        <SummaryRow
          label={CODES_COPY.fieldStarts}
          value={
            values.starts_at
              ? `${fmt(values.starts_at)} IST`
              : CODES_COPY.reviewStartsNow
          }
        />
        <SummaryRow
          label={CODES_COPY.fieldExpires}
          value={values.expires_at ? `${fmt(values.expires_at)} IST` : ""}
        />
        {"can_combine" in body && (
          <SummaryRow
            label={CODES_COPY.fieldCanCombine}
            value={body.can_combine ? "Yes" : "No"}
          />
        )}
        <SummaryRow label={CODES_COPY.fieldNote} value={body.private_note} />
      </SummarySection>
    </div>
  );
}

export default CodeFormModal;
